import React, { useState, useEffect } from 'react';
import FighterModal from '../components/FighterModal';
import { triggerAPIRequest } from '../services/api.methods';
import { Link } from 'react-router-dom';
import './Fighters.css';

const Fighters = ({ isLoggedIn }) => {
  const [fighters, setFighters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFighters = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const response = await triggerAPIRequest(
          'ufcdb',
          'db',
          'get_fighters_and_subs',
          { user_id: user.id || 0 },
          () => ({})
        );

        if (response.success && response.message.status === 'completed') {
          setFighters(response.message.result || []);
        } else {
          setError(typeof response.message === 'string' ? response.message : 'Failed to fetch fighters');
        }
      } catch (error) {
        console.error('Error fetching fighters:', error);
        setError('Failed to load fighters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFighters();
  }, []);

  const filteredFighters = fighters.filter(fighter =>
    fighter.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCardClick = (fighter) => {
    setSelectedFighter(fighter);
  };

  const handleSubscription = async (fighter, e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      setError('Please login to subscribe to fighters');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id) {
      setError('Please login to subscribe to fighters');
      return;
    }

    try {
      const action = fighter.subscription_status === '1' ? 'unsubscribe' : 'subscribe';
      const response = await triggerAPIRequest(
        'ufcdb',
        'db',
        'subscribe_to_fighter',
        {
          person_id: user.id,
          fighter_id: fighter.id,
          action: action
        },
        () => ({})
      );

      if (response.success) {
        // Update local state
        setFighters(prevFighters => prevFighters.map(f => {
          if (f.id === fighter.id) {
            return { ...f, subscription_status: action === 'subscribe' ? '1' : '0' };
          }
          return f;
        }));

        if (selectedFighter?.id === fighter.id) {
          setSelectedFighter(prev => ({
            ...prev,
            subscription_status: action === 'subscribe' ? '1' : '0'
          }));
        }
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      setError('Failed to update subscription. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading">Loading fighters...</div>;
  }

  if (error) {
    return <div className="error">{typeof error === 'string' ? error : 'An unexpected error occurred'}</div>;
  }

  return (
    <div className="fighters-page">
      {!isLoggedIn && (
        <div className="login-message">
          <h2>Please <Link to="/auth">log in</Link> to get notifications about your favorite fighters!</h2>
          <p>You can still browse fighters without logging in, but you won't be able to subscribe to them.</p>
        </div>
      )}

      <div className="notification-banner">
        Want to know when is your favorite fighter's next bout? {!isLoggedIn ? <Link to="/auth">Login</Link> : 'Click Sub'} and you will get notification!
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search fighters..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <button className="search-button">SEARCH</button>
      </div>

      <div className="fighters-grid">
        {filteredFighters.map(fighter => (
          <div
            key={fighter.id}
            className="fighter-card"
            onClick={() => handleCardClick(fighter)}
            style={{ cursor: 'pointer' }}
          >
            <div className="image-container">
              {fighter.fighterHeadshotImgUrl ? (
                <img
                  src={fighter.fighterHeadshotImgUrl}
                  alt={fighter.name}
                />
              ) : fighter.fighterFullBodyImgUrl ? (
                <img
                  src={fighter.fighterFullBodyImgUrl}
                  alt={fighter.name}
                  className="full-body-img"
                />
              ) : (
                <img
                  src="https://www.ufc.com/themes/custom/ufc/assets/img/no-profile-image.png"
                  alt="No profile"
                />
              )}
            </div>
            <div className="card-body">
              <div className="card-text-tags">
                {fighter.tags && fighter.tags.split(',').map((tag, index) => (
                  <span key={`regular-${fighter.id}-${index}`} className="card-text-tag">
                    {tag.startsWith('dwcs') ? 'dwcs' : tag}
                  </span>
                ))}
                {fighter.tagsDwcs && fighter.tagsDwcs.split(',').map((tag, index) => (
                  <span key={`dwcs-${fighter.id}-${index}`} className="card-text-tag">
                    {tag.startsWith('dwcs') ? 'dwcs' : tag}
                  </span>
                ))}
              </div>
              <h5>{fighter.name}</h5>
              <p>{fighter.record === "1-0-0" && fighter.sherdogRecord ? fighter.sherdogRecord : fighter.record}</p>
              <p>
                <i className="fa-regular fa-calendar-check"></i>
                &nbsp;{getNextFightDate(fighter)}
              </p>
              <div onClick={e => e.stopPropagation()}>
                <button
                  className={`fighter-button fighter-sub-button ${fighter.subscription_status === '1' ? 'subbed' : ''}`}
                  onClick={(e) => handleSubscription(fighter, e)}
                  disabled={!isLoggedIn}
                >
                  {fighter.subscription_status === '1' ? (
                    <><i className="fas fa-envelope"></i>&nbsp;Subbed</>
                  ) : (
                    <><i className="fa-solid fa-envelope-open"></i>&nbsp;{isLoggedIn ? 'Sub' : 'Login to Sub'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedFighter && (
        <FighterModal
          fighter={selectedFighter}
          onClose={() => setSelectedFighter(null)}
          onSubscribe={handleSubscription}
          isLoggedIn={isLoggedIn}
        />
      )}
    </div>
  );
};

// Helper function to get the next fight date
const getNextFightDate = (fighter) => {
  const isValidDate = (date) => {
    return date && date !== '0000-00-00' && date !== '';
  };

  if (isValidDate(fighter.nextFightDate)) {
    return fighter.nextFightDate.split('T')[0];
  }

  if (isValidDate(fighter.rumourNextFightDate)) {
    return fighter.rumourNextFightDate.split('T')[0];
  }

  return 'N/A';
};

export default Fighters;